import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

// Map: trigger variant ID -> attached variant ID
const LINKED_VARIANTS = {
  '46841389842658': '46840542789858', // 500 -> candle
  '46841389777122': '46840542757090', // 250 -> votive
  '47150869053666': '46840542757090', // 250
  '47150869119202': '46840542789858', // 500
};

let isSyncingLinkedItems = false;

async function syncLinkedItems() {
  if (isSyncingLinkedItems) return;
  isSyncingLinkedItems = true;

  try {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) return;

    const cart = await response.json();
    if (!cart?.items) return;

    // Desired quantities for attached variants, based on triggers
    /** @type {Record<string, number>} */
    const desiredAttachedQty = {};
    Object.values(LINKED_VARIANTS).forEach((attachedId) => {
      desiredAttachedQty[attachedId] = 0;
    });

    // Count trigger quantities
    for (const item of cart.items) {
      const idStr = String(item.id);
      const attachedId = LINKED_VARIANTS[idStr];

      if (attachedId) {
        desiredAttachedQty[attachedId] =
          (desiredAttachedQty[attachedId] || 0) + item.quantity;
      }
    }

    // Current quantities for attached variants
    /** @type {Record<string, number>} */
    const currentAttachedQty = {};
    for (const item of cart.items) {
      const idStr = String(item.id);
      if (Object.values(LINKED_VARIANTS).includes(idStr)) {
        currentAttachedQty[idStr] =
          (currentAttachedQty[idStr] || 0) + item.quantity;
      }
    }

    // Build /cart/update.js payload
    /** @type {Record<string, number>} */
    const updates = {};
    for (const [attachedId, targetQty] of Object.entries(desiredAttachedQty)) {
      const current = currentAttachedQty[attachedId] || 0;
      if (current !== targetQty) {
        updates[attachedId] = targetQty; // 0 removes, >0 sets/creates
      }
    }

    if (Object.keys(updates).length === 0) return;

    // Update cart (by variant ID)
    const updateRes = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!updateRes.ok) return;

    // Re-render all cart item sections (cart page + cart drawer)
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    const sectionIds = Array.from(cartItemsComponents)
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    await Promise.all(
      sectionIds.map((sectionId) =>
        sectionRenderer.renderSection(sectionId, { cache: false })
      )
    );
  } catch (e) {
    console.error('Error syncing linked cart items', e);
  } finally {
    isSyncingLinkedItems = false;
  }
}

// Run after any cart update (add, change, drawer changes, etc.)
document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (isSyncingLinkedItems) return;
  syncLinkedItems();
});